import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Google Analytics 4 Hook
 * Automatically initializes GA4 and tracks page views on route changes
 * Only active in production environment
 */
export function useGoogleAnalytics() {
  const location = useLocation();

  useEffect(() => {
    // Skip initialization in non-production environments
    if (import.meta.env?.MODE !== 'production') {
      console.log('GA4 tracking disabled in development mode');
      return;
    }

    // Check if GA4 Measurement ID is configured
    const measurementId = import.meta.env?.VITE_GA_MEASUREMENT_ID;
    if (!measurementId) {
      console.warn('GA4 Measurement ID not configured. Set VITE_GA_MEASUREMENT_ID in .env');
      return;
    }

    // Initialize gtag.js if not already done
    if (!window.dataLayer) {
      // Load gtag.js script dynamically
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      document.head?.appendChild(script);

      // Initialize dataLayer and gtag function
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() {
        window.dataLayer?.push(...arguments);
      };
      
      // Configure GA4
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        send_page_view: false // We'll manually send page views
      });

      console.log('GA4 initialized with ID:', measurementId);
    }

    // Send page_view event on route changes
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: location?.pathname + location?.search,
        page_title: document.title,
        page_location: window.location?.href
      });

      console.log('GA4 page view tracked:', location?.pathname);
    }
  }, [location]);
}

/**
 * Track custom GA4 events
 * @param {string} eventName - Name of the event (e.g., 'button_click', 'form_submit')
 * @param {object} eventParams - Additional event parameters
 */
export function trackEvent(eventName, eventParams = {}) {
  if (import.meta.env?.MODE !== 'production') {
    console.log('GA4 event (dev mode):', eventName, eventParams);
    return;
  }

  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, {
      ...eventParams,
      page: window.location?.pathname
    });
    console.log('GA4 event tracked:', eventName, eventParams);
  } else {
    console.warn('GA4 not initialized. Cannot track event:', eventName);
  }
}

/**
 * Track conversion events for bail bond inquiries
 * @param {string} conversionType - Type of conversion (e.g., 'phone_call', 'form_submission')
 * @param {object} conversionData - Additional conversion data
 */
export function trackConversion(conversionType, conversionData = {}) {
  trackEvent('conversion', {
    conversion_type: conversionType,
    ...conversionData
  });
}

/**
 * Track SEO-specific events
 * @param {string} seoAction - SEO action taken (e.g., 'schema_generated', 'meta_updated')
 * @param {object} seoData - Additional SEO data
 */
export function trackSEOEvent(seoAction, seoData = {}) {
  trackEvent('seo_action', {
    seo_action: seoAction,
    ...seoData
  });
}